import { useState, useEffect, useCallback, useRef } from 'react';
import { cacheService } from '@/shared/services/cacheService';
import { apiService } from '@/shared/services/api';
import { CACHE_KEYS, CACHE_CONFIG } from '@/shared/config/cache';
import logger from '@/shared/utils/logger';
import { backendErrorMessage, exceptionMessage } from '@/shared/utils/apiError';
import type { ApiResponse, GuideWithExercises } from '@/shared/types';
import type { UserSubmissionsPayload } from '@/submissions/types';

interface UseCachedApiOptions<T> {
  cacheKey: string;
  ttl: number;
  enabled?: boolean;
  onSuccess?: (data: T) => void;
  /** Recibe el mensaje ya normalizado, no la excepción. */
  onError?: (error: string) => void;
}

interface UseCachedApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseCachedApiReturn<T> extends UseCachedApiState<T> {
  refetch: () => Promise<void>;
  invalidateCache: () => void;
}

/**
 * Hook que integra caché con llamadas API
 * 
 * Flujo:
 * 1. Intenta cargar desde caché primero
 * 2. Si hay datos válidos en caché, los devuelve inmediatamente
 * 3. Si no hay caché o está expirado, hace la llamada API
 * 4. Guarda la respuesta en caché automáticamente
 * 5. Permite forzar refresh con refetch()
 */
export function useCachedApi<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  options: UseCachedApiOptions<T>
): UseCachedApiReturn<T> {
  const { cacheKey, ttl, enabled = true, onSuccess, onError } = options;


  const [state, setState] = useState<UseCachedApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  // Usar useRef para mantener una referencia estable a apiCall
  const apiCallRef = useRef(apiCall);
  apiCallRef.current = apiCall;

  // Flag para evitar múltiples llamadas simultáneas
  const isExecutingRef = useRef(false);

  const execute = useCallback(async (): Promise<T | null> => {
    if (!enabled || isExecutingRef.current) {
      return null;
    }

    isExecutingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiCallRef.current();

      if (response.success && response.data) {
        // Guardar en caché solo si no es un array vacío
        const isEmpty = Array.isArray(response.data) && response.data.length === 0;
        if (!isEmpty) {
          cacheService.set(cacheKey, response.data, ttl);
        }

        setState({
          data: response.data,
          loading: false,
          error: null,
        });

        // Callback de éxito
        if (onSuccess) {
          onSuccess(response.data);
        }

        return response.data;
      } else {
        const errorMessage = response.error?.message || 'Error desconocido';
        setState({
          data: null,
          loading: false,
          error: errorMessage,
        });

        if (onError) {
          onError(errorMessage);
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        backendErrorMessage(error) || exceptionMessage(error) || 'Error de conexión';
      setState({
        data: null,
        loading: false,
        error: errorMessage,
      });

      if (onError) {
        onError(errorMessage);
      }

      throw error;
    } finally {
      isExecutingRef.current = false;
    }
  }, [cacheKey, ttl, enabled, onSuccess, onError]); // Removido apiCall de las dependencias

  const refetch = useCallback(async () => {
    // Invalidar caché antes de hacer nueva llamada
    cacheService.invalidate(cacheKey);
    await execute();
  }, [execute, cacheKey]);

  const invalidateCache = useCallback(() => {
    cacheService.invalidate(cacheKey);
  }, [cacheKey]);

  // Efecto para cargar datos iniciales
  useEffect(() => {
    if (!enabled) {
      return;
    }

    logger.log(`[useCachedApi] Checking cache for key: ${cacheKey}`);

    // Intentar cargar desde caché primero
    const cachedData = cacheService.get<T>(cacheKey);

    if (cachedData) {
      logger.log(`[useCachedApi] Found cached data for key: ${cacheKey}`);
      // Hay datos válidos en caché, usarlos inmediatamente
      setState({
        data: cachedData,
        loading: false,
        error: null,
      });

      if (onSuccess) {
        onSuccess(cachedData);
      }
    } else {
      logger.log(`[useCachedApi] No cached data, making API call for key: ${cacheKey}`);
      // No hay caché válido, hacer llamada API
      execute();
    }
    // `execute` y `onSuccess` quedan fuera a propósito: `execute` cambia con cada
    // `onSuccess`/`onError` inline que pase el llamador y volvería a disparar la petición en
    // cada render. La carga sólo debe reintentarse al cambiar la clave de caché.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled]);

  return {
    ...state,
    refetch,
    invalidateCache,
  };
}

/**
 * Hook específico para obtener ejercicios disponibles con caché ETag-aware.
 *
 * - Cache válido (TTL no vencido): sirve datos locales sin petición de red.
 * - Cache vencido/ausente: petición condicional con If-None-Match.
 *   - 304 → refresca el TTL con los mismos datos, sin tocar la UI.
 *   - 200 → guarda nuevos datos + ETag, actualiza la UI.
 */
export function useCachedAvailableExercises() {
  const cacheKey = CACHE_KEYS.availableExercises;
  const ttl = CACHE_CONFIG.availableExercises;

  const [state, setState] = useState<UseCachedApiState<GuideWithExercises[]>>({
    data: null,
    loading: false,
    error: null,
  });

  // Prevent concurrent fetches (e.g. React StrictMode double-mount or fast remounts)
  const isExecutingRef = useRef(false);

  const fetchConditional = useCallback(async () => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const storedEtag = cacheService.getEtag(cacheKey);
      const result = await apiService.getAvailableExercisesConditional(storedEtag);

      if (result.notModified) {
        // Refrescar TTL con los mismos datos sin cambiar la UI
        const existing = cacheService.get<GuideWithExercises[]>(cacheKey);
        if (existing) cacheService.set(cacheKey, existing, ttl, storedEtag ?? undefined);
        setState(prev => ({ ...prev, loading: false }));
      } else {
        cacheService.set(cacheKey, result.data, ttl, result.etag ?? undefined);
        setState({ data: result.data, loading: false, error: null });
      }
    } catch (error) {
      const msg = backendErrorMessage(error) || exceptionMessage(error) || 'Error de conexión';
      setState({ data: null, loading: false, error: msg });
    } finally {
      isExecutingRef.current = false;
    }
  }, [cacheKey, ttl]);

  const refetch = useCallback(async () => {
    cacheService.invalidate(cacheKey);
    await fetchConditional();
  }, [cacheKey, fetchConditional]);

  const invalidateCache = useCallback(() => {
    cacheService.invalidate(cacheKey);
  }, [cacheKey]);

  useEffect(() => {
    const cached = cacheService.get<GuideWithExercises[]>(cacheKey);
    if (cached) {
      setState({ data: cached, loading: false, error: null });
    } else {
      fetchConditional();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, refetch, invalidateCache };
}

/**
 * Hook específico para obtener perfil de usuario con caché
 */
export function useCachedUserProfile(userId?: string) {
  return useCachedApi(
    () => apiService.getUserProfile(),
    {
      cacheKey: CACHE_KEYS.userProfile(userId),
      ttl: CACHE_CONFIG.userProfile,
    }
  );
}

/**
 * Hook específico para obtener submissions del usuario con caché, acotadas a un cuatrimestre.
 *
 * La primera carga va **sin** `term`: el default lo resuelve el backend (D34) y lo informa
 * en `payload.term`. Eso deja dos entradas de caché con el mismo contenido para el
 * cuatrimestre por defecto ("default" y el term concreto), aceptable con el TTL de 2 minutos
 * y preferible a una llamada previa sólo para averiguar el default.
 */
export function useCachedMySubmissions(userId?: string, term?: string) {
  return useCachedApi<UserSubmissionsPayload>(
    () => apiService.getMySubmissions(term),
    {
      cacheKey: CACHE_KEYS.mySubmissions(userId, term),
      ttl: CACHE_CONFIG.mySubmissions,
    }
  );
}
